import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { FlexColumn } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService } from 'ui-components/services/notification-service';

import { PhoneNumberCaptureForm } from '../../../../../../components/phone-number-capture/phone-number-capture-form';
import { useSavePhoneNumber } from '../../../../../../components/phone-number-capture/use-save-phone-number';
import { FTU_PHONE_CAPTURE_SAVE } from '../../../../../../constants/analytics-events';
import { Segment } from '../../../../../../services/analytics-services/segment';
import Global from '../../../../../../store/global-selectors';
import { messages } from './messages';
import style from './style.module.less';
import { US } from '../../../../../../constants/country-codes';

type PhoneNumberStepProps = {
  onValidationChange?: (isValid: boolean) => void;
  onSaveFunctionReady?: (saveFunction: () => Promise<void>) => void;
};

export const PhoneNumberStep = ({ onValidationChange, onSaveFunctionReady }: PhoneNumberStepProps) => {
  const intl = useIntl();
  const sellerCountry = useSelector(Global.selectSellerHomeCountry);
  const countryCode = sellerCountry || US;
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isConsentChecked, setIsConsentChecked] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleSuccess = useCallback(() => {
    Segment.trackEvent(FTU_PHONE_CAPTURE_SAVE);
    NotificationService.showNotification({
      notificationType: 'success',
      title: intl.formatMessage(messages.successMessage),
    });
  }, [intl]);

  const handleError = useCallback(() => {
    NotificationService.showNotification({
      notificationType: 'error',
      title: intl.formatMessage(messages.errorMessage),
    });
  }, [intl]);

  const { savePhoneNumber } = useSavePhoneNumber({
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const handlePhoneChange = useCallback((value: string) => {
    setPhoneNumber(value);
  }, []);

  const handleValidationChangeInternal = useCallback(
    (isValid: boolean) => {
      setIsFormValid(isValid);
      onValidationChange?.(isValid);
    },
    [onValidationChange],
  );

  const handleSave = useCallback(async () => {
    if (!isFormValid) {
      return;
    }
    await savePhoneNumber({ phoneNumber, isConsentChecked, countryCode });
  }, [savePhoneNumber, phoneNumber, isConsentChecked, countryCode, isFormValid]);

  // Expose the save function to the parent
  useEffect(() => {
    onSaveFunctionReady?.(handleSave);
  }, [handleSave, onSaveFunctionReady]);

  return (
    <FlexColumn className={style['phone-number-step']}>
      <FlexColumn className={style['phone-number-step-header']}>
        <Text className={style.header} textType={TextType.H2} value={messages.stepTitle} />
        <Text textType={TextType.Description} value={messages.stepDescription} />
      </FlexColumn>
      <div className={style['form-card']}>
        <div className={style['form-content']}>
          <PhoneNumberCaptureForm
            countryCode={countryCode}
            isConsentChecked={isConsentChecked}
            onConsentChange={setIsConsentChecked}
            onPhoneChange={handlePhoneChange}
            onValidationChange={handleValidationChangeInternal}
          />
        </div>
      </div>
    </FlexColumn>
  );
};
